import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import { SmoothScroll } from "@/components/layout/SmoothScroll";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import Home from "@/pages/Home";
import Brands from "@/pages/Brands";
import BrandDetail from "@/pages/BrandDetail";
import CategoryPage from "@/pages/CategoryPage";
import ServicePage from "@/pages/ServicePage";
import About from "@/pages/About";
import Stockist from "@/pages/Stockist";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import BrandsAdmin from "@/pages/admin/BrandsAdmin";
import CategoriesAdmin from "@/pages/admin/CategoriesAdmin";
import PromotionsAdmin from "@/pages/admin/PromotionsAdmin";
import HomepageAdmin from "@/pages/admin/HomepageAdmin";
import EnquiriesAdmin from "@/pages/admin/EnquiriesAdmin";

const SiteLayout = () => {
  const { pathname } = useLocation();
  return (
    <>
      <Navbar />
      <AnimatePresence mode="wait">
        <motion.div key={pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
          <Outlet />
        </motion.div>
      </AnimatePresence>
      <Footer />
    </>
  );
};

// Legacy deliverfood.com.au URLs were /<brand-slug>; map them to /brands/<slug> when a matching range exists.
const LegacyRedirect = () => {
  const { slug } = useParams();
  const [state, setState] = useState("checking");
  useEffect(() => { api.get(`/brands/${slug}`).then(() => setState("found")).catch(() => setState("missing")); }, [slug]);
  if (state === "checking") return <div className="min-h-screen" />;
  return state === "found" ? <Navigate to={`/brands/${slug}`} replace /> : <NotFound />;
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SmoothScroll>
              <Routes>
                <Route element={<SiteLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/brands" element={<Brands />} />
                  <Route path="/brands/:slug" element={<BrandDetail />} />
                  <Route path="/range/:slug" element={<CategoryPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/slice-house" element={<ServicePage slug="processing" />} />
                  <Route path="/private-label" element={<ServicePage slug="private-label" />} />
                  <Route path="/foodservice" element={<ServicePage slug="foodservice" />} />
                  <Route path="/brand-distribution" element={<ServicePage slug="brand-distribution" />} />
                  <Route path="/become-a-stockist" element={<Stockist />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/range-and-brands" element={<Navigate to="/brands" replace />} />
                  <Route path="/slicehouse" element={<Navigate to="/slice-house" replace />} />
                  <Route path="/food-processing" element={<Navigate to="/slice-house" replace />} />
                  <Route path="/stockist" element={<Navigate to="/become-a-stockist" replace />} />
                  <Route path="/:slug" element={<LegacyRedirect />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="brands" element={<BrandsAdmin />} />
                  <Route path="categories" element={<CategoriesAdmin />} />
                  <Route path="promotions" element={<PromotionsAdmin />} />
                  <Route path="homepage" element={<HomepageAdmin />} />
                  <Route path="enquiries" element={<EnquiriesAdmin />} />
                </Route>
              </Routes>
            </SmoothScroll>
          </AuthProvider>
          <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "Jost, sans-serif" } }} />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
